import get from 'lodash/get';
import { fromCents } from 'utils/helpers';
import * as actionTypes from 'actions/actionTypes';

const isErrorResponse = (error) => (
  // All errors are pushed to GA, except errors: 1401, 1474, 1414.
  (error && error.data && error.data.message
    && [1401, 1474, 1414].indexOf(error.data.errCode) === -1)
    || (error && error.message)
);

const parseErrorMessage = (error) => {
  let errorMessage = error.message ? error.message : error.data.message;
  const isErrorWithSensitiveInformation = errorMessage.match(/^(LV:|FI:|ES:|PL:|EE:|AU:|MX:|LT:).*/);
  if (isErrorWithSensitiveInformation) {
    // Remove sensitive infromation from error after first period.
    errorMessage = errorMessage.replace(/\..*$/i, '');
  }
  return errorMessage;
};

const getAnalyticsObject = (state, action) => {
  const { meta } = action;
  const { selfservice, authentication, analytics } = state;

  const serviceMap = {
    DueDateReminder: 'DUE_DATE_REMINDER',
    ChangeDueDate: 'CHANGE_DUE_DATE',
    PaymentHoliday: 'PAYMENT_HOLIDAY',
  };
  const userId = analytics ? analytics.userId : authentication.id;
  const contract = selfservice.contracts[authentication.contracts[0]];
  const contractId = contract ? contract.creditApplication.id : null;
  const applicationID = analytics ? analytics.applicationID : contractId;
  const sharedMetaData = {
    userId,
    applicationID,
    event: meta.event,
  };

  switch (meta.event) {
    case 'loginSelfservice': {
      const produtTypeFromMmps = contract.mmps?.length > 0 ? contract.mmps[contract.mmps.length - 1].product.type : '';
      const maxDraw = (contract.mmps && contract.mmps[contract.mmps.length - 1])
        ? contract.mmps[contract.mmps.length - 1].sum
        : 0;
      const productType = contract.product
        ? contract.product.type
        : produtTypeFromMmps;
      const availableDraw = contract.availableLimit
        ? contract.availableLimit
        : maxDraw;
      const availableUpgrade = contract.upgrade.product
        ? fromCents(contract.upgrade.product.principal).toFixed(2)
        : 0;
      return {
        ...sharedMetaData,
        pageView: 'dashboardSelfservice',
        product: productType,
        availableDraw: fromCents(availableDraw).toFixed(2),
        availableUpgrade,
      };
    }
    case 'DueDateReminder':
    case 'ChangeDueDate':
    case 'PaymentHoliday': {
      const selectedService = contract[serviceMap[meta.event]];
      const price = selectedService.price ? fromCents(selectedService.price).toFixed(2) : '0.00';

      return {
        ...sharedMetaData,
        event: 'otherSelfservices',
        page: meta.event === 'ChangeDueDate' ? 'dueDateChange' : 'enablePeace',
        amount: price,
      };
    }
    case 'purchaseSelfservice': {
      return {
        ...sharedMetaData,
        product: 'CL-UPGRADE',
        drawAmount: '0.00',
        newCL_Amount: fromCents(get(contract, ['upgrade', 'product', 'principal'], 0)).toFixed(2),
        oldCL_Amount: fromCents(contract.product.principal).toFixed(2),
      };
    }
    case 'DrawCompletedSelfservice': {
      return {
        ...sharedMetaData,
        event: 'DrawSelfservice',
        page: 'drawDone',
        product: 'CL-DRAW',
        drawAmount: fromCents(meta.amount).toFixed(2),
      };
    }
    case 'checkoutSelfservice': {
      return {
        ...sharedMetaData,
        pageView: 'contractChanges_overview',
        product: meta.product,
      };
    }
    case 'DrawSelfservice': {
      return {
        ...sharedMetaData,
        page: 'drawConfirm',
        product: 'CL-DRAW',
        drawAmount: fromCents(meta.amount).toFixed(2),
      };
    }
    case 'bannerSelfservice': {
      return {
        ...sharedMetaData,
        bannerType: meta.bannerType,
      };
    }
    case 'reject': {
      return {
        ...sharedMetaData,
        product: meta.product,
        alert_msg: isErrorResponse(meta.error) ? parseErrorMessage(meta.error) : '',
      };
    }
    default:
      return null;
  }
};

export default (store) => (next) => (action) => {
  if (!(window.dataLayer && window.dataLayer.push)) {
    return next(action);
  }
  const {
    meta,
    type,
    error,
  } = action;
  let analyticsData = null;
  if (type === actionTypes.TRACK_ANALYTICS_DATA && meta && meta.event) {
    const state = store.getState();

    const dataToPush = getAnalyticsObject(state, action);
    if (dataToPush) {
      analyticsData = dataToPush;
    }
  }

  if (isErrorResponse(error)) {
    const errorMessage = parseErrorMessage(error);
    analyticsData = { event: 'error_msg', alert_msg: errorMessage };
  }

  if (analyticsData) {
    if (DEBUG) {
      console.table(analyticsData);
    }
    window.dataLayer.push(analyticsData);
  }

  return next(action);
};
